/**
 * @fileoverview Component for displaying a QR code.
 * This component includes a QR code and a button for downloading it.
 */

import { XMarkIcon } from '@heroicons/react/24/outline'
import { useRef } from 'react'
import QR from 'react-qr-code'
import BackIcon from '../../../assets/back.svg'
import DownloadIcon from '../../../assets/download.svg'
import { useModal } from '../../../hooks/useModal'


/**
 * @interface QRCodeProps
 * @description Props for the QRCode component.
 * @property {Novoic.API.AssignmentInvitation} invitation - The invitation object.
 * @property {string} title - The title of the QR code.
 * @property {boolean} downloadable - Whether the QR code can be downloaded.
 * @property {function} onBack - The function to call when the back button is clicked.
 */
interface QRCodeProps {
    invitation: Novoic.API.AssignmentInvitation
    title?: string
    downloadable?: boolean
    onBack?: () => void
}

/**
 * @function QRCode
 * @description Component for displaying a QR code.
 * @param {QRCodeProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the QR code and a button for downloading it.
 */
export function QRCode({ invitation, title, downloadable, onBack }: QRCodeProps) {
    const [, close] = useModal()

    const qrCodeRef = useRef(null)

    const downloadQRCode = () => {
        // get the QR Code SVG element
        const svg = qrCodeRef.current.querySelector('svg')

        // create a new canvas element
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')

        // set canvas size
        canvas.width = 1024
        canvas.height = 1024

        // create a new image element
        const img = new Image()

        // when the image is loaded, draw it to the canvas
        img.onload = function () {
            context.drawImage(img, 0, 0, 1024, 1024)

            // create a new anchor element and simulate a click
            const a = document.createElement('a')
            a.download = title + ' QR code.png' // change to 'QRCode.jpg' for jpg format
            a.href = canvas.toDataURL('image/png') // change to 'image/jpeg' for jpg format
            a.click()
        }

        // start loading the image
        img.src = 'data:image/svg+xml,' + encodeURIComponent(svg.outerHTML)
    }

    return (
        <div className="w-[416px] h-[436px]">
            <div className="flex justify-between items-center gap-4 mb-6">
                {onBack && (
                    <div className="flex h-[28px]">
                        <BackIcon onClick={onBack} className="cursor-pointer fill-gray-900" />
                    </div>
                )}
                {title && <h2 className="block font-semibold text-xl text-gray-900">{title}</h2>}
                <button className="w-6 h-6" onClick={close}>
                    <XMarkIcon />
                </button>
            </div>
            <div ref={qrCodeRef} className="flex justify-center px-[64px] pt-[42px] items-center ">
                <QR value={invitation.link} />
            </div>
            {downloadable && (
                <div className="flex justify-center mt-9 items-center gap-x-2">
                    <button
                        onClick={downloadQRCode}
                        className="inline-flex text-sm font-medium text-indigo-700 items-center px-3 py-2"
                    >
                        <DownloadIcon />
                        Download QR
                    </button>
                </div>
            )}
        </div>
    )
}
